import { ref } from 'vue'
import { useFormDataStore } from '@/entries/interact-form/stores/formData'
import type { FormItem, FormDataStoreMinimal, ApiResponse } from '@/entries/interact-form/types'

// FormDataStoreの型をuseFormDataStoreの戻り値から取得
type FormDataStore = ReturnType<typeof useFormDataStore>

export function useContactForm(contactformId: string, contactformHtml: string) {
  const isFormSubmitted = ref(false)
  const isSubmitDisabled = ref(true)

  // Contact Form 7のHTMLにhiddenフィールドと戻るボタンを追加
  function injectFormDataToHtml(html: string, formData: FormDataStore): string {
    // 処理済みデータを取得
    const processedData = formData.processFormData()

    // プレーンテキスト版
    const questionsAndAnswers = processedData.questionsAndAnswersText

    // HTML版
    const formattedHtml = processedData.formattedHtml
    
    // JSON版（データ保存用）
    const formDataJson = JSON.stringify(formData.formDetails)
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')

    // トリガー設問データを取得（processFormDataの返り値に含まれていると仮定）
    const triggersText = processedData.triggersText || ''
    const triggersHtml = processedData.triggersHtml || ''
    const triggersPlainText = processedData.triggersPlainText || ''

    // デバッグログ追加: page-titleにセットされる値を出力
    console.log('[injectFormDataToHtml] page-title:', processedData.pageTitle)
    // hidden フィールドを作成（複数のフォーマットで保存）
    const hiddenFields = `
      <input type="hidden" name="form-data" value="${formDataJson}">
      <input type="hidden" name="page-title" value="${processedData.pageTitle}">
      <input type="hidden" name="form_data_text" value="${questionsAndAnswers.replace(/"/g, '&quot;')}">
      <input type="hidden" name="form_data_html" value="${formattedHtml.replace(/"/g, '&quot;')}">
      <input type="hidden" name="trigger_data" value="${triggersText.replace(/"/g, '&quot;')}">
      <input type="hidden" name="trigger_data_html" value="${triggersHtml.replace(/"/g, '&quot;')}">
      <input type="hidden" name="trigger_data_plain" value="${triggersPlainText.replace(/"/g, '&quot;')}">
    `

    // 戻るボタンを含むHTMLを作成
    const backButtonHtml = `<button type="button" class="back-to-questions-button" onclick="window.backToQuestions()">質問に戻る</button>`

    // interact-form-submitクラスの要素を特定し、その中に戻るボタンを追加
    if (html.includes('<div class="interact-form-submit">')) {
      // submit ボタンの前に戻るボタンを挿入
      html = html.replace(
        '<div class="interact-form-submit">',
        '<div class="interact-form-submit">' + backButtonHtml,
      )
    }

    // フォームに一意のIDを追加（contactformIdを使用）
    html = html.replace('<form', `<form id="interact-contact-form-${contactformId}"`)
    
    // フォーム終了タグの前に hidden フィールドを挿入
    return html.replace('</form>', `${hiddenFields}</form>`)
  }

  // 必須フィールドのバリデーションを設定する関数
  function setupRequiredFieldsValidation() {
    // フォーム要素を取得
    const form = document.querySelector('.wpcf7-form')
    if (!form) return

    // 送信ボタンを取得
    const submitButton = form.querySelector('.wpcf7-submit') as HTMLInputElement
    if (!submitButton) return

    // 必須フィールドを取得
    const requiredFields = form.querySelectorAll('[aria-required="true"]')

    // 初期状態でボタンを無効化しない（クリック時にバリデーションを行う）
    isSubmitDisabled.value = true
    updateSubmitButtonVisual(submitButton, isSubmitDisabled.value)

    // 各必須フィールドのblurイベントを監視
    requiredFields.forEach((field) => {
      const inputElement = field as HTMLInputElement

      // エラーメッセージ表示用の要素を追加
      addErrorMessageElement(inputElement)

      // 入力時のバリデーションは削除し、フォーカスが外れたときのみバリデーション
      inputElement.addEventListener('blur', () => {
        validateField(inputElement)

        // すべてのフィールドのバリデーション状態を確認し、ボタンの表示を更新
        const allValid = validateAllRequiredFieldsWithoutErrors(requiredFields)
        isSubmitDisabled.value = !allValid
        updateSubmitButtonVisual(submitButton, isSubmitDisabled.value)
      })
    })

    // フォーム送信時のバリデーション
    form.addEventListener('submit', (event) => {
      // すべての必須フィールドをバリデーション
      const isValid = validateAllRequiredFields(requiredFields)

      // バリデーションに失敗した場合は送信をキャンセル
      if (!isValid) {
        event.preventDefault()
        event.stopPropagation()

        // 最初のエラーフィールドにフォーカス
        const firstErrorField = form.querySelector('.error-field') as HTMLInputElement
        if (firstErrorField) {
          firstErrorField.focus()
        }
      }
    })
  }

  // 個別フィールドのバリデーション
  function validateField(inputElement: HTMLInputElement): boolean {
    const inputType = inputElement.type.toLowerCase()
    const errorElement = document.getElementById(`error-${inputElement.name}`)
    let isValid = true
    let errorMessage = ''

    // 空値チェック
    if (!inputElement.value.trim()) {
      isValid = false
      errorMessage = '必須項目です'
    } else {
      // type属性に応じたバリデーション
      switch (inputType) {
        case 'email':
          if (!validateEmail(inputElement.value)) {
            isValid = false
            errorMessage = '有効なメールアドレスを入力してください'
          }
          break

        case 'tel':
          if (!validatePhoneNumber(inputElement.value)) {
            isValid = false
            errorMessage = '有効な電話番号を入力してください'
          }
          break

        // 他のタイプに応じたバリデーション
      }
    }

    // エラー表示の更新
    if (errorElement) {
      errorElement.textContent = errorMessage
      errorElement.style.display = isValid ? 'none' : 'block'
    }

    // 入力フィールドのスタイル更新
    inputElement.classList.toggle('error-field', !isValid)

    return isValid
  }

  // エラーメッセージ要素の追加
  function addErrorMessageElement(inputElement: HTMLInputElement) {
    // 既存のエラー要素があれば削除
    const existingError = document.getElementById(`error-${inputElement.name}`)
    if (existingError) {
      existingError.remove()
    }

    // 新しいエラー要素を作成
    const errorElement = document.createElement('div')
    errorElement.id = `error-${inputElement.name}`
    errorElement.className = 'form-error-message'
    errorElement.style.display = 'none'

    // 入力要素の後に挿入
    inputElement.parentNode?.insertBefore(errorElement, inputElement.nextSibling)
  }

  // 全フィールドのバリデーション（エラー表示あり）
  function validateAllRequiredFields(fields: NodeListOf<Element>): boolean {
    let isValid = true

    for (const field of fields) {
      const inputElement = field as HTMLInputElement
      // 各フィールドのバリデーション結果を保持しつつ、すべてのフィールドをチェック
      const fieldValid = validateField(inputElement)
      isValid = isValid && fieldValid
    }

    return isValid
  }

  // 全フィールドのバリデーション（エラー表示なし）
  function validateAllRequiredFieldsWithoutErrors(fields: NodeListOf<Element>): boolean {
    for (const field of fields) {
      const inputElement = field as HTMLInputElement
      const inputType = inputElement.type.toLowerCase()

      // 空値チェック
      if (!inputElement.value.trim()) {
        return false
      }

      // type属性に応じたバリデーション
      switch (inputType) {
        case 'email':
          if (!validateEmail(inputElement.value)) {
            return false
          }
          break

        case 'tel':
          if (!validatePhoneNumber(inputElement.value)) {
            return false
          }
          break

        case 'number':
          if (isNaN(Number(inputElement.value))) {
            return false
          }
          break

        case 'url':
          if (!validateUrl(inputElement.value)) {
            return false
          }
          break

        case 'date':
          if (!validateDate(inputElement.value)) {
            return false
          }
          break
      }
    }

    return true
  }

  // 各フィールドのバリデーション
  function areAllRequiredFieldsFilled(fields: NodeListOf<Element>): boolean {
    for (const field of fields) {
      const inputElement = field as HTMLInputElement
      const inputType = inputElement.type.toLowerCase()

      // 空値チェック（基本的なバリデーション）
      if (!inputElement.value.trim()) {
        return false
      }

      // type属性に応じた追加バリデーション
      switch (inputType) {
        case 'email':
          // メールアドレスの形式チェック
          if (!validateEmail(inputElement.value)) {
            return false
          }
          break

        case 'tel':
          // 電話番号の形式チェック
          if (!validatePhoneNumber(inputElement.value)) {
            return false
          }
          break

        case 'number':
          // 数値チェック
          if (isNaN(Number(inputElement.value))) {
            return false
          }
          break

        case 'url':
          // URL形式チェック
          if (!validateUrl(inputElement.value)) {
            return false
          }
          break

        case 'date':
          // 日付形式チェック
          if (!validateDate(inputElement.value)) {
            return false
          }
          break

        // 他のタイプに応じたバリデーションを追加
      }
    }
    return true
  }

  // メールアドレスのバリデーション
  function validateEmail(email: string): boolean {
    const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
    return re.test(email.toLowerCase())
  }

  // 電話番号のバリデーション（日本の電話番号形式）
  function validatePhoneNumber(phone: string): boolean {
    // ハイフンありなしどちらも許容
    const re = /^(0\d{1,4}-?\d{1,4}-?\d{4})$/
    return re.test(phone)
  }

  // URL形式のバリデーション
  function validateUrl(url: string): boolean {
    try {
      new URL(url)
      return true
    } catch (e) {
      return false
    }
  }

  // 日付形式のバリデーション
  function validateDate(dateStr: string): boolean {
    const date = new Date(dateStr)
    return !isNaN(date.getTime())
  }

  // 送信ボタンの視覚的な状態を更新する関数
  function updateSubmitButtonVisual(button: HTMLInputElement, isDisabled: boolean) {
    // ボタンは常に有効だが、視覚的な状態を変更
    button.disabled = false

    // CSSクラスで視覚的な状態を変更
    if (isDisabled) {
      button.classList.add('inactive')
    } else {
      button.classList.remove('inactive')
    }
  }

  // Contact Form 7のイベントリスナーを設定
  function setupContactForm7EventListeners(onSubmitSuccess: () => void) {
    console.log('[useContactForm] Setting up Contact Form 7 event listeners')
    
    // フォーム送信成功時のイベント
    document.addEventListener(
      'wpcf7mailsent',
      function (event: Event) {
        // カスタムイベントとして型キャスト
        const customEvent = event as CustomEvent<{
          contactFormId: string
          status: string
          apiResponse: ApiResponse
        }>

        // フォーム送信成功時の処理
        console.log('[useContactForm] wpcf7mailsent event fired', customEvent.detail)

        // 特定のフォームIDの場合のみ処理する
        if (customEvent.detail.contactFormId === contactformId) {
          console.log('[useContactForm] Form ID matched, processing submission')
          // フォームデータを処理
          onSubmitSuccess()
          // サンクスページへ遷移
          console.log('[useContactForm] Redirecting to thanks page')
          window.location.href = '/taiwa-thanks'
        } else {
          console.log('[useContactForm] Form ID mismatch', {
            expected: contactformId,
            received: customEvent.detail.contactFormId
          })
        }
      },
      false,
    )

    // フォーム送信前のイベント
    document.addEventListener(
      'wpcf7beforesubmit',
      function (event: Event) {
        const customEvent = event as CustomEvent<{
          contactFormId: string
        }>

        console.log('[useContactForm] wpcf7beforesubmit event fired', {
          formId: customEvent.detail.contactFormId,
          url: window.location.href
        })
      },
      false,
    )

    // 送信完了後のイベント
    document.addEventListener(
      'wpcf7submit',
      function (event: Event) {
        const customEvent = event as CustomEvent<{
          contactFormId: string
          status: string
        }>

        console.log('[useContactForm] wpcf7submit event fired', {
          formId: customEvent.detail.contactFormId,
          status: customEvent.detail.status
        })
      },
      false,
    )
  }

  // マウント時にContact Form 7の送信済み状態を確認する関数
  function checkFormSubmissionStatus(contactformId: string): boolean {
    // Contact Form 7の送信済み状態を確認
    const formElement = document.querySelector(`.wpcf7-form[data-form-id="${contactformId}"]`)

    if (formElement) {
      // 送信済みかどうかを判定（クラス名で判断する例）
      if (
        formElement.classList.contains('sent') ||
        formElement.getAttribute('data-status') === 'sent'
      ) {
        console.log('フォームは既に送信済みです')
        isFormSubmitted.value = true
        return true
      }
    }

    // ローカルストレージに送信済みフラグがあるか確認する例
    const submissionKey = `cf7_form_submitted_${contactformId}`
    if (localStorage.getItem(submissionKey) === 'true') {
      console.log('ローカルストレージに送信済みフラグがあります')
      isFormSubmitted.value = true
      return true
    }

    return false
  }

  return {
    isFormSubmitted,
    isSubmitDisabled,
    injectFormDataToHtml,
    setupRequiredFieldsValidation,
    setupContactForm7EventListeners,
    checkFormSubmissionStatus,
  }
}
